"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid } from "@radix-ui/themes";
import { LotWithSortingData } from "./page";
import {
  AuctionSettings,
  AuctionState,
  AuctionType,
  BiddingSchedule,
  BiddingScheduleRange,
  Lot,
  ResultsPublishedStatus,
  BidType,
  Role,
} from "@prisma/client";
import LotPreview from "./LotPreview";
import { Skeleton } from "@/app/components/ui/skeleton";
import { fetcher } from "@/lib/utils";
import useSWR from "swr";
import { LotLiveData } from "@/app/api/auctions/[auctionId]/gridData/route";
import { drop, take } from "lodash";
import {
  AuctionFilterContext,
  AuctionFilterContextProps,
  Filter,
  FilterType,
} from "@/lib/context/AuctionFilterContext";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { WinningStatus } from "@/lib/enum/winStatus";
import { HttpStatusCode } from "axios";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import _ from "lodash";
import { debounce } from "lodash";
import { AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE } from "@/lib/constants";
import { SoftCloseContext } from "@/lib/context/SoftCloseContext";
import { getNthNextBid } from "@/lib/bidding/util";
import { isAfter } from 'date-fns';
import { LotSearcher } from "@/lib/search/LotSearcher";
import LiveCloseTransitionDialog from "./LiveCloseTransitionDialog";
import { useOneTimeNotification } from "@/app/hooks/useOneTimeNotification";
import HybridInfoDialog from "./HybridInfoDialog";
import { useSession } from "next-auth/react";

interface Props {
  allLots: LotWithSortingData[];
  auctionStart: Date;
  auctionState: AuctionState;
  auctionType: AuctionType;
  auctionSettings: AuctionSettings;
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
}

const DEBOUNCE_INTERVAL = 150; // ms
// Catalogue data can never fall more than 5 seconds out of sync.
const REFRESH_INTERVAL = 4_000; // ms

// TODO: Paginate data-loading based off searchParams
const AuctionCatalogue = ({
  allLots,
  auctionStart,
  auctionState,
  auctionType,
  auctionSettings,
  biddingSchedule,
}: Props) => {
  const searchParams = useSearchParams();
  const { auctionId } = useParams();
  const { data: session }: any = useSession();

  const page = parseInt(searchParams.get("page") || "1");
  const pageSize = parseInt(
    searchParams.get("pageSize") ||
    AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE.toString()
  );

  const [lots, setLots] = useState<LotWithSortingData[]>(allLots);
  const auctionFilterContext = useContext(AuctionFilterContext);
  const [forceRender, setForceRender] = useState(0);
  const softCloseContext = useContext(SoftCloseContext);
  const router = useRouter();

  // Live-close dialog
  const [liveCloseDialogOpen, setLiveCloseDialogOpen] = useState(false);
  const prevAuctionStateRef = useRef<AuctionState | null>(null);

  // One-time hybrid info dialog
  const isHybrid = auctionType === AuctionType.HYBRID;
  const { shouldShow: shouldShowHybridInfo } = useOneTimeNotification(
    isHybrid ? `hybrid-auction-info` : undefined
  );
  const [hybridInfoOpen, setHybridInfoOpen] = useState(false);

  const {
    data: liveAuctionState,
    mutate: mutateAuctionState,
  } = useSWR<AuctionState>(
    `/api/auctions/${auctionId}/auctionState`,
    fetcher,
    {
      fallbackData: auctionState,
      revalidateOnMount: true,
    }
  );

  // TODO: Don't display grid times until this isValidating is false.
  const {
    data: lotGridData,
    isLoading,
    mutate: mutateLotGridData,
  } = useSWR<{ [key: number]: LotLiveData }>(
    `/api/auctions/${auctionId}/gridData`,
    fetcher,
    {
      refreshInterval: REFRESH_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  );

  const { data: winningStatus, mutate: mutateWinningStatus } = useSWR<{
    [key: number]: WinningStatus;
  }>(
    `/api/auctions/${auctionId}/gridData/winning-status`,
    async (url) => {
      const res = await fetch(url);
      if (res.status === HttpStatusCode.NoContent) {
        // Return null if 204 status code is received
        return null;
      }
      return res.json();
    },
    {
      refreshInterval: REFRESH_INTERVAL,
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const onAuctionStateChange = useCallback((payload: any) => {
    if (payload?.auctionState) {
      mutateAuctionState(payload.auctionState);
    }
  }, [mutateAuctionState]);

  const updateLotGridData = useCallback(
    (
      payload: any,
      lotId: number,
      updateFunction: () => Partial<LotLiveData>
    ) => {
      mutateLotGridData(
        (currentData: { [key: number]: LotLiveData } | undefined) => {
          if (!currentData) return currentData;
          if (!currentData[lotId]) return currentData;
          return {
            ...currentData,
            [lotId]: {
              ...currentData[lotId],
              ...updateFunction(),
            },
          };
        },
        false
      );
      mutateWinningStatus();
    },
    [mutateLotGridData, mutateWinningStatus]
  );

  const onLotChange = useCallback(
    (payload: any) => {
      if (payload?.endDate && payload?.id) {
        updateLotGridData(payload, payload.id, () => ({
          endDate: payload.endDate,
        }));
      }
    },
    [updateLotGridData]
  );

  const onHighestBidChange = useCallback((payload: any) => {
    mutateWinningStatus();
    if (payload?.amount && payload?.lotId) {
      updateLotGridData(
        payload,
        payload.lotId,
        () => ({
          highestBid: payload.amount,
          nextBid: getNthNextBid(payload.amount, biddingSchedule, 1)
        })
      );
    }
  }, [updateLotGridData, mutateWinningStatus, biddingSchedule]);

  const auctionTopic = `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Auction${auctionId}`;
  const lotTopic = `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Auction${auctionId}.Lot`;
  const highestBidTopic = `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.HighestBid`;

  useSharedSubscription({
    topic: auctionTopic,
    onMessage: onAuctionStateChange,
  });

  useSharedSubscription({
    topic: highestBidTopic,
    onMessage: onHighestBidChange,
  });

  useSharedSubscription({ topic: lotTopic, onMessage: onLotChange });

  // TOOD: What happens if this fails?
  const { data: imageKeys } = useSWR(
    `/api/image/auctions/${auctionId}?limit=1`,
    fetcher
  );

  useEffect(() => {
    setForceRender((prev) => prev + 1);
  }, [softCloseContext?.newCloseCount]);

  // Detect transition into LIVE_CLOSE and show dialog
  useEffect(() => {
    if (!liveAuctionState) return;
    if (prevAuctionStateRef.current == null) {
      prevAuctionStateRef.current = liveAuctionState;
      return;
    }
    if (
      prevAuctionStateRef.current !== AuctionState.LIVE_CLOSE &&
      liveAuctionState === AuctionState.LIVE_CLOSE
    ) {
      setLiveCloseDialogOpen(true);
    }
    prevAuctionStateRef.current = liveAuctionState;
  }, [liveAuctionState]);

  // Open one-time hybrid info dialog if applicable
  useEffect(() => {
    if (isHybrid && shouldShowHybridInfo && session?.role !== Role.ADMIN) {
      setHybridInfoOpen(true);
    }
  }, [isHybrid, shouldShowHybridInfo]);

  const filteredLots = useMemo(() => {
    const initialFilteredLots = filterLots(allLots, auctionFilterContext);
    return initialFilteredLots.filter((lot) => {
      if (auctionState === AuctionState.POST_AUCTION) {
        switch (auctionSettings.resultsPublished) {
          case ResultsPublishedStatus.PUBLISHED_WITHOUT_UNSOLD:
            return lot.HighestBid && lot.HighestBid.length > 0;
          case ResultsPublishedStatus.PUBLISHED_WITH_UNSOLD:
            return true;
          default:
            return false;
        }
      }
      const endDate = lotGridData?.[lot.id]?.endDate || lot.endDate;
      return endDate && isAfter(new Date(endDate), new Date());
    });
  }, [
    allLots,
    auctionFilterContext,
    forceRender,
    lotGridData,
    auctionState,
    auctionSettings,
  ]);

  const sortedLots = useMemo(() => {
    // If there's an active search, preserve the relevance sorting from filterBySearch
    if (auctionFilterContext?.search) {
      return filteredLots;
    }
    // Otherwise, apply manual sorting
    return sortLots(filteredLots, searchParams.get("orderBy"), lotGridData);
  }, [filteredLots, searchParams, auctionFilterContext?.search, lotGridData]);

  const debouncedFilter = useCallback(() => {
    const filterFn = () => {
      auctionFilterContext?.setItemCount(filteredLots.length);

      if ((page - 1) * pageSize >= sortedLots.length && sortedLots.length > 0) {
        const params = new URLSearchParams(searchParams);
        params.set("page", "1");
        router.replace("?" + params.toString());
        return;
      }

      const paginatedLots = take(
        drop(sortedLots, (page - 1) * pageSize),
        pageSize
      );

      setLots(paginatedLots);
    };

    return debounce(filterFn, DEBOUNCE_INTERVAL);
  }, [
    auctionFilterContext,
    page,
    pageSize,
    router,
    searchParams,
    sortedLots,
    filteredLots,
    forceRender,
  ])();

  useEffect(() => {
    debouncedFilter();
  }, [debouncedFilter]);

  if (!liveAuctionState) {
    return null;
  }

  if (
    auctionState === AuctionState.POST_AUCTION &&
    auctionSettings.resultsPublished === ResultsPublishedStatus.UNPUBLISHED
  ) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        Auction results have not yet been published.
      </div>
    );
  }

  if (lots.length === 0) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        No lots matching the filtering criteria were found.
      </div>
    );
  }

  const liveCloseDialog = (
    <LiveCloseTransitionDialog
      open={liveCloseDialogOpen}
      onOpenChange={setLiveCloseDialogOpen}
      onConfirm={() => {
        router.refresh();
        setLiveCloseDialogOpen(false);
      }}
      durationMs={10000}
    />
  );

  const hybridInfoDialog = (
    <HybridInfoDialog open={hybridInfoOpen} onOpenChange={setHybridInfoOpen} liveClose={false} />
  );

  return imageKeys ? (
    <>
      {liveCloseDialog}
      {hybridInfoDialog}
      <Grid
      columns={{ initial: "2", sm: "3", md: "4" }}
      gapX={{ initial: "1", sm: "7" }}
      gapY={{ initial: "9", sm: "8" }}
      className="w-full"
    >
      {lots.map((lot, index) => {
        if (!lotGridData?.[lot.id])
          return (
            <Skeleton key={index} className="w-full h-[350px] rounded-none" />
          );

        return (
          <LotPreview
            key={lot.id}
            auctionId={auctionId as string}
            lot={lot}
            imageKey={imageKeys?.[lot.id]}
            polledData={lotGridData[lot.id]}
            followers={lotGridData[lot.id].followers ?? 0}
            following={lotGridData[lot.id].following ?? false}
            winningStatus={winningStatus?.[lot.id]}
            auctionType={auctionType}
            auctionStart={auctionStart}
            auctionState={liveAuctionState}
            loading={isLoading}
          />
        );
      })}
      </Grid>
    </>
  ) : (
    <>
      {liveCloseDialog}
      {hybridInfoDialog}
      <Grid
      columns={{ xs: "2", sm: "3", md: "4" }}
      gapX="7"
      gapY="8"
      width="auto"
    >
      {lots.map((_, index) => (
        <Skeleton key={index} className="w-full h-[350px] rounded-none" />
      ))}
      </Grid>
    </>
  );
};

function filterLots(
  lots: LotWithSortingData[],
  auctionFilterContext: AuctionFilterContextProps | null
) {
  let ret = filterByCategory(
    lots,
    auctionFilterContext?.getFiltersOfType(FilterType.CATEGORY)
  );
  ret = filterByBudget(
    ret,
    auctionFilterContext?.getFiltersOfType(FilterType.BUDGET)
  );
  const searcher = new LotSearcher(ret);
  return searcher.search(auctionFilterContext?.search);
}

const filterByCategory = (
  lots: LotWithSortingData[],
  categoryFilters: Filter[] | undefined
) => {
  if (!categoryFilters || categoryFilters.length === 0) return lots;
  return lots.filter((lot) =>
    categoryFilters.some((filter) => filter.label === lot.category)
  );
};

const filterByBudget = (
  lots: LotWithSortingData[],
  budgetFilters: Filter[] | undefined
) => {
  if (!budgetFilters || budgetFilters.length === 0) return lots;

  return lots.filter((lot) => {
    const currentBid = lot?.HighestBid?.[0]?.amount ?? lot.startBid;

    // Huge code smell here.
    return budgetFilters.every((filter) => {
      const label = filter.label.toLowerCase();
      if (label.startsWith("budget: above $")) {
        const threshold = parseInt(label.replace("budget: above $", ""));
        return currentBid > threshold;
      } else if (label.startsWith("budget: below $")) {
        const threshold = parseInt(label.replace("budget: below $", ""));
        return currentBid && currentBid < threshold;
      } else if (label.startsWith("budget: $")) {
        const [min, max] = label
          .replace("budget: $", "")
          .split(" to $")
          .map(Number);
        return currentBid >= min && currentBid <= max;
      }
      return true;
    });
  });
};

function sortLots(lots: LotWithSortingData[], sortParam: string | null, lotGridData?: { [key: number]: LotLiveData }) {
  const [field, order] = sortParam ? sortParam.split(",") : ["", ""];

  if (order !== "asc" && order !== "desc")
    return _.orderBy(lots, [(lot) => lot.catalogNumber], ["asc"]);

  switch (field) {
    case "lot":
      return _.orderBy(lots, [(lot) => lot.catalogNumber], [order]);
    case "activeBid":
      return _.orderBy(lots,
        [
          // First sort by whether there is a highest bid (creates two groups)
          (lot) => lot.HighestBid.length === 0,
          // Within groups, sort by highest bid amount or start bid
          (lot) => lot.HighestBid.length > 0 ? lot.HighestBid[0].amount : lot.startBid,
          // Maintain consistent ordering within equal values
          (lot) => lot.catalogNumber
        ],
        [
          'asc', // Keep bids with HighestBid first
          order,
          'asc'
        ]);
    case "bid":
      return _.orderBy(
        lots,
        [
          (lot) => {
            if (lot.HighestBid.length > 0) {
              return lot.HighestBid[0].amount;
            }
            if (lot.endDate && (new Date()).getTime() > (new Date(lot.endDate)).getTime()) {
              return -Infinity;
            }
            return lot.startBid;
          },
          (lot) => lot.catalogNumber,
        ],
        [order, "asc"]
      );
    case "bidCount":
      return _.orderBy(
        lots,
        [
          (lot) => lot.Bid.filter((b) => !b.removed && b.type === BidType.STANDARD).length,
          (lot) => lot.catalogNumber,
        ],
        [order, "asc"]
      );
    case "watchCount":
      return _.orderBy(
        lots,
        [
          (lot) => lotGridData?.[lot.id]?.followers ?? 0, 
          (lot) => lot.catalogNumber
        ],
        [order, "asc"]
      );
    default:
      return lots;
  }
}

export default AuctionCatalogue;
